'use client';
import { useState, useCallback } from 'react';
import { Download, Share2, Star, StarOff, Copy, Check, FileText, ExternalLink } from 'lucide-react';
import { useTheme } from '@/components/ThemeContext';

/* ══════════════════════════════════════════════
   에이전트 공통 유틸리티 바
   C. PDF 내보내기
   F. 분석 결과 공유 링크
   B. NAS 프로젝트 즐겨찾기
   ══════════════════════════════════════════════ */

interface AgentToolbarProps {
  title: string;
  content: string;
  agentType: string;
  projectId?: string;
  projectName?: string;
}

export function AgentToolbar({ title, content, agentType, projectId, projectName }: AgentToolbarProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sharing, setSharing] = useState(false);

  // C. PDF 내보내기 — 브라우저 인쇄 다이얼로그 활용
  const handleExportPDF = useCallback(async () => {
    if (!content) return;
    setExporting(true);
    try {
      const res = await fetch('/api/agent-utils', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'export-html', title, content, agentType, metadata: { projectId } })
      });
      const html = await res.text();
      const w = window.open('', '_blank');
      if (w) { w.document.write(html); w.document.close(); }
    } catch (e) { console.error('PDF export failed:', e); }
    setExporting(false);
  }, [title, content, agentType, projectId]);

  // F. 공유 링크 생성
  const handleShare = useCallback(async () => {
    if (!content) return;
    setSharing(true);
    try {
      const res = await fetch('/api/agent-utils', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create-share', title, content, agentType })
      });
      const data = await res.json();
      if (data.shareUrl) {
        setShareUrl(data.shareUrl);
        await navigator.clipboard.writeText(data.shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      }
    } catch (e) { console.error('Share failed:', e); }
    setSharing(false);
  }, [title, content, agentType]);

  // B. 즐겨찾기 토글
  const handleToggleFavorite = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await fetch('/api/agent-utils', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'toggle-favorite', projectId, projectName: projectName || projectId })
      });
      const data = await res.json();
      setIsFavorite(data.action === 'added');
    } catch (e) { console.error('Favorite toggle failed:', e); }
  }, [projectId, projectName]);

  if (!content) return null;

  const btnClass = `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold cursor-pointer transition hover:opacity-80 ${
    isDark ? 'bg-white/5 border border-white/10 text-gray-400 hover:text-white' : 'bg-gray-50 border border-gray-200 text-gray-500 hover:text-gray-700'
  }`;

  return (
    <div className={`flex items-center gap-2 flex-wrap p-3 rounded-xl border ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-gray-50 border-gray-100'}`}>
      {/* C. PDF 내보내기 */}
      <button onClick={handleExportPDF} disabled={exporting} className={btnClass}>
        {exporting ? <FileText className="w-3 h-3 animate-pulse" /> : <Download className="w-3 h-3" />}
        PDF 리포트
      </button>

      {/* F. 공유 링크 */}
      <button onClick={handleShare} disabled={sharing} className={btnClass}>
        {copied ? <Check className="w-3 h-3 text-emerald-400" /> : sharing ? <Share2 className="w-3 h-3 animate-spin" /> : <Share2 className="w-3 h-3" />}
        {copied ? '링크 복사됨!' : '공유 링크'}
      </button>

      {/* B. 즐겨찾기 */}
      {projectId && (
        <button onClick={handleToggleFavorite} className={btnClass}>
          {isFavorite ? <Star className="w-3 h-3 text-amber-400 fill-amber-400" /> : <StarOff className="w-3 h-3" />}
          {isFavorite ? '즐겨찾기' : '즐겨찾기 추가'}
        </button>
      )}

      {/* 공유 URL 표시 */}
      {shareUrl && (
        <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] ${isDark ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-emerald-50 text-emerald-600 border border-emerald-200'}`}>
          <ExternalLink className="w-3 h-3" />
          <span className="truncate max-w-[200px]">{shareUrl}</span>
          <button onClick={() => { navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }} className="cursor-pointer">
            <Copy className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}

export default AgentToolbar;
